import { defineStore } from "pinia"
import { ref, computed } from "vue"
import axios from "axios"
import { isRouteOnlyForGuests, isRoutePublic, router } from "@/router"
import { useNotificationStore } from "./notification-store"
import { useBannerStore } from "./banner-store"
import { useToastStore } from "./toast-store"
import { useGeneralStore } from "./general-store"
import { useRouter } from "vue-router"
import type { RBAC_PERMISSION } from "@server/api/rbac"

/**
 * Use this function to redirect the Vue router to whatever redirect intent is
 * provided in the redirect query string.
 *
 * @param  {boolean}  toDashboard  If set, the function will redirect to
 *                                 dashboard if no intent is set.
 */
function redirectToIntent (toDashboard: boolean = false): void {
  // If the user attempted to view another page, let's redirect them there
  // now. Otherwise, back to the homepage.
  const { query } = router.currentRoute.value
  if ("redirect" in query && !Array.isArray(query.redirect) && query.redirect !== null) {
    router.replace(query.redirect).catch(err => { console.error("Could not redirect to intent", err) })
  } else if (toDashboard) {
    router.replace({ path: "/" }).catch(err => console.error("Could not navigate to dashboard", err))
  }
}

/**
 * Perform actions that are necessary only once after a successful login.
 */
function onceAfterLogin () {
  const notificationStore = useNotificationStore()
  const bannerStore = useBannerStore()
  const toastStore = useToastStore()
  const generalStore = useGeneralStore()

  // First, check for notification settings. If they are set to "default", this
  // means we have not yet asked for permissions -> do that now.
  if (notificationStore.notificationPermission === "default" && notificationStore.notificationAPIAvailable) {
    bannerStore.show({
      message: `${generalStore.appName} does not have permission to show native notifications. With native notifications, we can inform you about, e.g., successful or failed exports, new workspaces, and other changes without you needing to be online.`,
      type: "info",
      actions: ["Enable"],
      actionCallback () {
        void notificationStore.requestNativeNotificationPermission()
      },
      onDismiss () {
        toastStore.toast("You can always make a decision from the notification center and/or your browser.", "info")
      }
    })
  }
}

export const useUserStore = defineStore("user", () => {
  const notificationStore = useNotificationStore()
  const router = useRouter()

  // The three primitive data points we need to handle the signed in user
  const sessionUUID = ref<null|string>(null)
  
  // The user object containing all their data
  const user = ref<PrivateUserJSON|null>(null)

  // One-off ref that indicates whether the app is still loading (affects the
  // initial refresh token attempt)
  const isLoading = ref(true)

  async function fetchUserData (): Promise<void> {
    const response = await axios.post("/api/user/get-data")
    sessionUUID.value = response.data.sessionUUID
    user.value = response.data.user
  }

  // Initial fetching
  fetchUserData()
    .then(() => {
      if (user.value !== null) {
        onceAfterLogin()
        void router.isReady().then(() => {
          // Same thing as below: If we want to redirect the user, we have to
          // let the router finish their initial navigation.
          if (isRouteOnlyForGuests(router.currentRoute.value)) {
            router.push({ name: "dashboard" }).catch(err => console.error("Could not navigate to dashboard", err))
          }
        })
        // Also fetch the notifications, now that we're online.
        notificationStore.fetchNotifications().catch(err => console.error(err))
      }
    })
    .catch(() => {
      // User is not logged in -> redirect
      user.value = null
      // Before we redirect, we must wait until the router is ready, since the
      // router will ALWAYS start at / and then navigates to wherever the user
      // pointed first. Only then will the "currentRoute" be the correct one
      // that the user indicated. If the promise resolves before the vue router
      // has finished the initial navigation, then the currentRoute would be the
      // dashboard which is not public. If we left this out, users are not able
      // to visit any page.
      void router.isReady().then(() => {
        if (!isRoutePublic(router.currentRoute.value)) {
          const fullPath = router.currentRoute.value.fullPath
          router.push({ name: "login", query: { redirect: fullPath } }).catch(err => console.error("Could not navigate to login", err))
        }
      })
    })
    .finally(() => {
      isLoading.value = false
    })

  // Every 10 minutes, check if the user is still logged in. If that isn't the
  // case, redirect to the login page. TODO: Also do this from various other
  // stores programmatically, if axios responds with a 401 error.
  setInterval(() => {
    fetchUserData().catch(() => {
      const wasLoggedIn = user.value !== null
      user.value = null
      // If the user was logged in previously, redirect them back to the login
      // page. If they were never logged in (public guests), keep them wherever
      // they are. (This also makes sure the sign up page never gets unloaded.)
      if (wasLoggedIn) {
        const fullPath = router.currentRoute.value.fullPath
        router.push({ name: "login", query: { redirect: fullPath } }).catch(err => console.error("Could not navigate to login", err))
      }
    })
  }, 1000 * 60 * 10)

  /**
   * Attempts to log in the user with the provided credentials. Stores them in
   * the store.
   *
   * @param   {string}  email     The email
   * @param   {string}  password  The password
   */
  async function attemptLogin (email: string, password: string): Promise<void> {
    const response = await axios.post("/api/auth/login", { email, password })
    sessionUUID.value = response.data.sessionUUID
    user.value = response.data.user

    onceAfterLogin()
    // Without intent, we still want to redirect the user to the dashboard on
    // manual login
    redirectToIntent(true)
  }

  async function logout (): Promise<void> {
    await axios.post("/api/auth/terminate-session", { sessionUUID: sessionUUID.value })
    user.value = null
    router.push({ name: "login" }).catch(err => console.error("Could not navigate to login", err))
  }

  /**
   * Tiny helper utility to quickly check for one or more permissions of this
   * user. (For a list of all, check the server RBAC module: server/rbac/index.ts)
   *
   * @return  {boolean} Whether the permission is granted for this user.
   */
  function perm (permission: RBAC_PERMISSION|RBAC_PERMISSION[]): boolean {
    if (user.value === null) {
      return false
    }

    if (Array.isArray(permission)) {
      return permission.every(perm => user.value!.permissions.includes(perm))
    } else {
      return user.value.permissions.includes(permission)
    }
  }
  
  /**
   * Function that checks whether the user in question has ANY admin permission.
   * (For a list of all, check the server RBAC module: server/rbac/index.ts)
   *
   * @return  {boolean} True or false
   */
  const hasAnyAdminPermission = computed(() => {
    if (user.value === null) {
      return false
    }

    // NOTE: Potentially not very smart to do it this way
    return user.value.permissions.find(p => p.startsWith("admin.")) !== undefined
  })

  return { isLoading, attemptLogin, logout, fetchUserData, user, sessionUUID, perm, hasAnyAdminPermission }
})
